/**
 * Phase 41 Plan 41-05 Task 1 — NeedsApprovalCard.
 *
 * Manager+ dashboard card; rendered conditionally by DashboardPage when
 * `isManager && !isImpersonating && !isFreelance` (Pitfall 5).
 *
 * Wraps `useApprovalCounts` (60s refetch — Pitfall 7) for the headline count
 * + a "My team / All" scope toggle that controls which destination the
 * "View all" affordance routes to. The toggle is local state (NOT a route)
 * per the plan's behavior spec.
 *
 * F-14 (2026-05-13 UAT batch Phase 3) — PM persona is a first-class
 * "team scope" along with manager. The backend now scopes
 * `adminJoinRequests` to the PM's own projects (see
 * approvalCountService.ts F-19 branch), so PMs see Join requests as a
 * team-scope bucket — same as Timesheets and Expenses. They never see
 * Time off (manager / HR only) nor `adminTimesheets` (admin-only). The
 * "All" toggle stays admin-only.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../ui/Card';
import { useApprovalCounts } from '../../hooks/useApprovalCounts';
import { useAuth } from '../../services/authContext';

type Scope = 'team' | 'all';

interface ScopeToggleProps {
  scope: Scope;
  onChange: (next: Scope) => void;
  isAdmin: boolean;
}

function ScopeToggle({ scope, onChange, isAdmin }: ScopeToggleProps) {
  return (
    <div className="flex text-[10.5px] rounded-md overflow-hidden border border-ms-border">
      <button
        type="button"
        onClick={() => onChange('team')}
        className={
          scope === 'team'
            ? 'px-2 py-1 bg-ms-text-strong text-white'
            : 'px-2 py-1 bg-white text-ms-text-secondary hover:bg-ms-surface-2'
        }
      >
        My team
      </button>
      <button
        type="button"
        onClick={() => onChange('all')}
        disabled={!isAdmin}
        className={
          scope === 'all'
            ? 'px-2 py-1 bg-ms-text-strong text-white'
            : 'px-2 py-1 border-l border-ms-border bg-white text-ms-text-secondary hover:bg-ms-surface-2 disabled:opacity-40 disabled:cursor-not-allowed'
        }
      >
        All
      </button>
    </div>
  );
}

export function NeedsApprovalCard() {
  const { isAdmin, isPM } = useAuth();
  const [scope, setScope] = useState<Scope>('team');
  const { data: counts, isLoading } = useApprovalCounts(true);

  // F-14 — Join requests is a team-scope bucket for PMs (backend scopes
  // adminJoinRequests by projectManagerId for non-admins). For admins
  // it stays under the "All" toggle so the headline isn't double-
  // counted with their cross-org adminTimesheets entry.
  const teamJoinRequests = isPM && !isAdmin ? counts?.adminJoinRequests ?? 0 : 0;
  const teamSum =
    (counts?.managerTimesheets ?? 0) +
    (counts?.managerExpenses ?? 0) +
    (counts?.managerTimeOffs ?? 0) +
    teamJoinRequests;
  const allSum = teamSum + (counts?.adminTimesheets ?? 0) + (counts?.adminJoinRequests ?? 0);
  const total = scope === 'all' && isAdmin ? allSum : teamSum;

  const buckets: { label: string; count: number; href: string }[] = [
    {
      label: 'Timesheets',
      count: counts?.managerTimesheets ?? 0,
      href: '/manager/timesheets/pending',
    },
    {
      label: 'Expenses',
      count: counts?.managerExpenses ?? 0,
      href: '/manager/expenses/pending',
    },
    {
      label: 'Time off',
      count: counts?.managerTimeOffs ?? 0,
      href: '/manager/time-off/pending',
    },
  ];
  // F-14 — PM-persona team scope includes Join requests.
  if (isPM && !isAdmin) {
    buckets.push({
      label: 'Join requests',
      count: counts?.adminJoinRequests ?? 0,
      href: '/admin/join-requests',
    });
  }
  if (scope === 'all' && isAdmin) {
    buckets.push({
      label: 'Admin timesheets',
      count: counts?.adminTimesheets ?? 0,
      href: '/admin/timesheets/pending',
    });
    buckets.push({
      label: 'Join requests',
      count: counts?.adminJoinRequests ?? 0,
      href: '/admin/join-requests',
    });
  }

  return (
    <Card
      title={`Needs your approval${total > 0 ? ` · ${total}` : ''}`}
      action={<ScopeToggle scope={scope} onChange={setScope} isAdmin={isAdmin} />}
    >
      {isLoading ? (
        <div className="text-[12px] text-ms-text-muted">Loading…</div>
      ) : total === 0 ? (
        <div className="text-[12px] text-ms-text-muted">Nothing pending — nice.</div>
      ) : (
        <ul className="divide-y divide-ms-border">
          {buckets
            .filter((b) => b.count > 0)
            .map((b) => (
              <li key={b.label}>
                <Link
                  to={b.href}
                  className="flex items-center justify-between py-2 text-[12.5px] hover:bg-ms-surface-2 -mx-2 px-2 rounded"
                >
                  <span className="text-ms-text-secondary">{b.label}</span>
                  <span className="font-semibold tabular-nums text-ms-text-strong">{b.count}</span>
                </Link>
              </li>
            ))}
        </ul>
      )}
    </Card>
  );
}
